import React from "react";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Autocomplete,
  Typography,
} from "@mui/material";
import Langs from "../../../../../app/lang/langs";
import { getLastUpdatedFromDate } from "../../../../../app/utils/helpers";
import { INPUT_SX, SELECT_SX, ORANGE } from "../constants";
import { timestampToDateStr, timestampToTimeStr } from "../utils/formHelpers";
import FieldRow from "./FieldRow";
import InfoRow from "./InfoRow";

export default function ShiftBody({
  selectedFormData,
  selectedForm,
  formError,
  dynamicLeaves,
  settings,
  handlers,
  dataFns,
}) {
  const {
    handleNativeDateFromChange,
    handleNativeDateToChange,
    handleNativeTimeFromChange,
    handleNativeTimeToChange,
    handleShiftTypeChange,
    handleLeaveTypeChange,
    handleGroupeChange,
    handleResourceChange,
    handleClientChange,
    handleSetAbsent,
    handleNotesChange,
  } = handlers;

  const {
    getSelectedStandardDayPeriod,
    getFromDayPeriodOptions,
    getToDayPeriodOptions,
    getCollabGroupes,
    getGroupeResources,
    getClientsOptions,
  } = dataFns;

  return (
    <Box sx={{ px: 2.5, pt: 2, pb: 12 }}>
      {/* Start date + time */}
      <FieldRow label="START_DATE">
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={timestampToDateStr(selectedFormData.from)}
            onChange={handleNativeDateFromChange}
            sx={{ ...INPUT_SX, flex: 1 }}
            inputProps={{ style: { fontSize: "16px" } }}
          />
          {selectedFormData.type == "s" ? (
            <FormControl size="small" sx={{ ...SELECT_SX, minWidth: 120 }}>
              <Select
                value={getSelectedStandardDayPeriod(selectedFormData.from)}
                onChange={(e) => handlers.onTimeFromChange(e.target.value * 60)}
                sx={{ fontSize: "16px" }}
              >
                {getFromDayPeriodOptions().map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Langs str={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              type="time"
              size="small"
              value={timestampToTimeStr(selectedFormData.from)}
              onChange={handleNativeTimeFromChange}
              sx={{ ...INPUT_SX, minWidth: 110 }}
              inputProps={{ style: { fontSize: "16px" } }}
            />
          )}
        </Box>
      </FieldRow>

      {/* End date + time */}
      <FieldRow label="END_DATE">
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={timestampToDateStr(selectedFormData.to)}
            onChange={handleNativeDateToChange}
            disabled={
              !["AST"].includes(selectedFormData.type) &&
              !dynamicLeaves[selectedFormData.type]
            }
            sx={{ ...INPUT_SX, flex: 1 }}
            inputProps={{ style: { fontSize: "16px" } }}
          />
          {selectedFormData.type == "s" ? (
            <FormControl size="small" sx={{ ...SELECT_SX, minWidth: 120 }}>
              <Select
                value={getSelectedStandardDayPeriod(selectedFormData.to)}
                onChange={(e) => handlers.onTimeToChange(e.target.value * 60)}
                sx={{ fontSize: "16px" }}
              >
                {getToDayPeriodOptions().map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Langs str={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              type="time"
              size="small"
              value={timestampToTimeStr(selectedFormData.to)}
              onChange={handleNativeTimeToChange}
              sx={{ ...INPUT_SX, minWidth: 110 }}
              inputProps={{ style: { fontSize: "16px" } }}
            />
          )}
        </Box>
      </FieldRow>

      {/* Type */}
      {selectedForm === "SHIFT" ? (
        <FieldRow label="TYPE">
          <FormControl fullWidth size="small" sx={SELECT_SX}>
            <Select
              value={selectedFormData.type || ""}
              onChange={(e) => handleShiftTypeChange(e.target.value)}
              sx={{ fontSize: "16px" }}
            >
              {(settings.shiftTypes || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Langs str={opt.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldRow>
      ) : (
        <FieldRow label="TYPE" error={formError === "MISSING_TYPE"}>
          <TextField
            fullWidth
            size="small"
            value={selectedFormData.type || ""}
            onChange={handleLeaveTypeChange}
            inputProps={{ maxLength: 50, style: { fontSize: "16px" } }}
            error={formError === "MISSING_TYPE"}
            sx={INPUT_SX}
          />
        </FieldRow>
      )}

      {/* Groupe */}
      <FieldRow label="GROUPE">
        <FormControl fullWidth size="small" sx={SELECT_SX}>
          <Select
            value={selectedFormData.id_groupe || ""}
            onChange={(e) => handleGroupeChange(e.target.value)}
            sx={{ fontSize: "16px" }}
          >
            {getCollabGroupes().map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FieldRow>

      {/* Assigned collab */}
      <FieldRow label="ASSIGNED">
        <FormControl fullWidth size="small" sx={SELECT_SX}>
          <Select
            value={selectedFormData.id_collab || ""}
            onChange={(e) => handleResourceChange(e.target.value)}
            sx={{ fontSize: "16px" }}
          >
            {getGroupeResources().map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FieldRow>

      {/* Client */}
      <FieldRow label="CLIENT">
        <Autocomplete
          options={getClientsOptions()}
          getOptionLabel={(opt) => opt.label || ""}
          isOptionEqualToValue={(opt, val) => opt.value == val.value}
          value={
            getClientsOptions().find(
              (o) =>
                o.value ==
                (selectedFormData.id_team_delegate || selectedFormData.id_team || null)
            ) || null
          }
          onChange={(_e, opt) => handleClientChange(opt ? opt.value : null)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              sx={INPUT_SX}
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  style: { fontSize: "16px" },
                },
              }}
            />
          )}
          slotProps={{
            popper: { sx: { zIndex: 1500 } },
          }}
        />
      </FieldRow>

      {/* Absent */}
      <FieldRow label="ABSENT">
        <RadioGroup
          row
          value={selectedFormData.absent ? "yes" : "no"}
          onChange={(e) => handleSetAbsent(e.target.value === "yes")}
        >
          <FormControlLabel
            value="yes"
            control={<Radio sx={{ "&.Mui-checked": { color: ORANGE } }} />}
            label={<Typography sx={{ fontSize: "0.9rem" }}><Langs str="YES" /></Typography>}
          />
          <FormControlLabel
            value="no"
            control={<Radio sx={{ "&.Mui-checked": { color: ORANGE } }} />}
            label={<Typography sx={{ fontSize: "0.9rem" }}><Langs str="NO" /></Typography>}
          />
        </RadioGroup>
      </FieldRow>

      {/* Description */}
      <FieldRow label="DESCRIPTION">
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          value={(selectedFormData && selectedFormData.description) || ""}
          onChange={handleNotesChange}
          inputProps={{ maxLength: 200, style: { fontSize: "16px" } }}
          sx={INPUT_SX}
        />
      </FieldRow>

      {/* Meta info */}
      {selectedFormData && selectedFormData.createdBy && (
        <InfoRow label="CREATED_BY" value={selectedFormData.createdBy} />
      )}
      {selectedFormData && selectedFormData.updatedAt && (
        <InfoRow
          label="LAST_UPDATED"
          value={
            <>
              {getLastUpdatedFromDate(selectedFormData.updatedAt)}
              {selectedFormData.updatedBy && (
                <span>
                  {" "}
                  <Langs str="by" /> {selectedFormData.updatedBy}
                </span>
              )}
            </>
          }
        />
      )}

      {/* Error */}
      {formError && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: "12px", fontSize: "0.85rem" }}>
          <Langs str={formError} />
        </Alert>
      )}
    </Box>
  );
}
